import { useEffect, useMemo, useState } from 'react'

import {
  defaultPaymentFilters,
  filterPayments,
  type PaymentsFilters,
} from '../data/paymentFilters'
import {
  apiGetPayments,
  apiGetPaymentSummary,
  type PaymentSummary,
} from '../api/payments/paymentsApi'
import type { PaymentRecord } from '../types'

export const usePayments = () => {
  const [records, setRecords] = useState<PaymentRecord[]>([])
  const [summary, setSummary] = useState<PaymentSummary | null>(null)
  const [filters, setFilters] = useState<PaymentsFilters>(
    defaultPaymentFilters,
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    let alive = true
    let running = false

    const load = async () => {
      if (!alive || running) return

      running = true

      try {
        const [paymentsResult, summaryResult] = await Promise.all([
          apiGetPayments(controller.signal),
          apiGetPaymentSummary(controller.signal),
        ])

        if (!alive) return

        const messages: string[] = []

        if (paymentsResult.success && paymentsResult.data) {
          const data = paymentsResult.data

          setRecords(data)
          setSelectedId((current) =>
            data.some((payment) => payment.id === current)
              ? current
              : data[0]?.id ?? null,
          )
        } else {
          setRecords([])
          setSelectedId(null)
          messages.push(paymentsResult.message)
        }

        if (summaryResult.success && summaryResult.data) {
          setSummary(summaryResult.data)
        } else {
          setSummary(null)
          messages.push(summaryResult.message)
        }

        setError(messages.join(' ') || null)
      } catch (error) {
        if (alive) {
          setRecords([])
          setSummary(null)
          setError(
            error instanceof Error
              ? error.message
              : 'Could not load payments.',
          )
        }
      } finally {
        running = false

        if (alive) setIsLoading(false)
      }
    }

    void load()

    const onFocus = () => {
      void load()
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void load()
      }
    }

    const timer = window.setInterval(onVisible, 30_000)

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      alive = false
      controller.abort()
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const visible = useMemo(
    () => filterPayments(records, filters),
    [records, filters],
  )

  const selected = useMemo(
    () =>
      visible.find((payment) => payment.id === selectedId)
      ?? visible[0]
      ?? null,
    [visible, selectedId],
  )

  return {
    records,
    visible,
    summary,
    filters,
    setFilters,
    selected,
    selectPayment: setSelectedId,
    resetFilters: () => setFilters(defaultPaymentFilters),
    isLoading,
    error,
  }
}